import { html, nothing, unsafeCSS, type CSSResultGroup } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { LibElement } from '../lib-element';
import css from './lib-carousel-indicator.css';

/** Slide navigation with connected bars or spaced steps. */
@customElement('lib-carousel-indicator')
export class LibCarouselIndicator extends LibElement {
  protected override get hostFocusSelector(): string { return 'button'; }
  static override readonly styles: CSSResultGroup = [LibElement.hostStyles, unsafeCSS(css)];

  @property() variant = 'stepper';
  @property({ type: Number, attribute: 'slide-count' }) slideCount = 1;
  @property({ type: Number, attribute: 'current-slide' }) currentSlide = 1;
  @property({ attribute: 'aria-label' }) override ariaLabel = 'Carousel slide navigation';
  @property({ attribute: 'slide-label' }) slideLabel = 'Go to slide {0}';

  get value(): string { return String(this.currentSlide); }

  get #count(): number { return Math.max(1, Math.trunc(this.slideCount) || 1); }

  protected override willUpdate(): void {
    this.currentSlide = Math.min(this.#count, Math.max(1, Math.trunc(this.currentSlide) || 1));
  }

  override render() {
    return html`<div class="lib-carousel-indicator lib-carousel-indicator-variant-${this.variant}"
      role="group" aria-label=${this.ariaLabel}>
      ${Array.from({ length: this.#count }, (_, index) => {
        const slide = index + 1;
        const active = slide === this.currentSlide;
        return html`<button type="button"
          class="lib-carousel-indicator-item ${active ? 'lib-carousel-indicator-item-active' : ''}"
          aria-label=${this.slideLabel.replace('{0}', String(slide))}
          aria-current=${active ? 'true' : nothing} @click=${() => this.#select(slide)}>
          <span class="lib-carousel-indicator-bar" aria-hidden="true"></span>
        </button>`;
      })}
    </div>`;
  }

  #select(slide: number): void {
    if (slide === this.currentSlide) return;
    this.currentSlide = slide;
    this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
  }
}

declare global {
  interface HTMLElementTagNameMap { 'lib-carousel-indicator': LibCarouselIndicator; }
}
